import {  SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Table from '../../../../components/Table/Table';
import { RawProd } from '../../../../services/rawProd/rawProd.types';
import { Buttons, SelectRawProdsContainer, StyledTableWrapper } from './SelectRawProds.styles';
import { SelectRawProdsProps } from './SelectRawProds.types';
import RawProductSelectTable from "../../../../components/RawProduct/RawProductSelectTable";

const SelectRawProds: FC<SelectRawProdsProps> = ({
  selectedRawProds,
  setSelectedRawProds,
  setStep,
  flow = 'removeRawProds',
}) => {
  const availableRawProductGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [rawProdNo, setRawProdNo] = useState('');
  const [rawProdNoError, setRawProdNoError] = useState(false);

  const selectAvailableRawProds = (selectedRows: RawProd[]) => {
    availableRawProductGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find(
        (selectedRawProd) => selectedRawProd.moaRawProdNo === node?.data?.moaRawProdNo
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  useEffect(() => {
    if (selectedRawProds) {
      selectedGridRef?.current?.api?.selectAll();
      selectAvailableRawProds(selectedRawProds);
    }
  });

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedRawProds(
      event.api.getSelectedRows().map((row) => {
        const existsInSelected = selectedRawProds.find((rawProd) => rawProd.moaRawProdNo === row.moaRawProdNo);
        return {
          ...row,
          usageAmount:
            flow === 'removeRawProds' ? existsInSelected?.usageAmount || 0 : existsInSelected?.usageAmount || '',
        };
      })
    );
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAvailableRawProds(event.api.getSelectedRows());
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRawProdNo(event.target.value);
    setRawProdNoError(false);
  };

  const handleRemoveRawProd = (event: MouseEvent) => {
    event.preventDefault();
    let foundRecord = false;
    availableRawProductGridRef?.current?.api?.forEachNode((node) => {
      if (node.data.moaRawProdNo.toString() === rawProdNo) {
        node.setSelected(true);
        foundRecord = true;
      }
    });
    if (!foundRecord) {
      setRawProdNoError(true);
    }
  };

  const handleKeyDown = () => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      let foundRecord = false;
      availableRawProductGridRef?.current?.api?.forEachNode((node) => {
        if (node.data.moaRawProdNo.toString() === rawProdNo) {
          node.setSelected(true);
          foundRecord = true;
        }
      });
      if (!foundRecord) {
        setRawProdNoError(true);
      }
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const stepInstructionsText = () => {
    if (flow === 'addRawProds') {
      return <H2>Select raw products to add and input usage amount</H2>;
    }
    if (flow === 'currentRawProduct') {
      return <H2>Select the current raw product to be changed</H2>;
    }
    if (flow === 'desiredRawProduct') {
      return <H2>Select the desired raw product to replace with and input usage amount</H2>;
    }
    if (flow === 'removeRawProds') {
      return <H2>Select raw products to remove</H2>;

    }
  };

  const selectedTableHeaderTitle = () => {
    if (flow === 'currentRawProduct') {
      return 'Selected Raw Product';
    }
    if (flow === 'desiredRawProduct') {
      return 'Desired Raw Product';
    }
    return 'Selected Raw Products';
  };

  const handleNextClick = () => {
    if (flow === 'addRawProds' || flow === 'currentRawProduct' || flow === 'removeRawProds') setStep(1);
  };

  return (
    <SelectRawProdsContainer>
      {stepInstructionsText()}
      <TableRowWrapper>
        <RawProductSelectTable
            ref={availableRawProductGridRef}
            title={'Available Raw Products'}
            onSelected={onSelectionChangedAvailable}
            rowSelection={flow === 'currentRawProduct' || flow === 'desiredRawProduct' ? 'single' : 'multiple'}
            rowMultiSelectWithClick={true}
        />
        <StyledTableWrapper>
          <Table
            ref={selectedGridRef}
            showHeader={true}
            headerTitle={selectedTableHeaderTitle()}
            headerButtons={[
              {
                inputProps: {
                  label: '',
                  id: 'removeRawProd',
                  testId: 'removeRawProdInputSearch',
                  errorText: rawProdNoError ? 'Raw product not found' : '',
                  onChange: handleChange,
                  'aria-label': 'remove raw product',
                  ref: inputRef,
                  onKeyDown: handleKeyDown()
                },
                buttonProps: {
                  label: 'Remove Raw Product',
                  testId: 'removeRawProdSearchBoxActionButton',
                  onClick: handleRemoveRawProd,
                },
              },
            ]}
            columnDefs={[
              {
                minWidth: 70,
                maxWidth: 70,
                flex: 1,
                headerName: 'Select',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 120, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
              { minWidth: 220, flex: 2, field: 'moaRawProdDesc', headerName: 'Description' },
            ]}
            rowData={selectedRawProds}
            rowSelection={flow === 'currentRawProduct' || flow === 'desiredRawProduct' ? 'single' : 'multiple'}
            suppressRowClickSelection={true}
            onSelectionChanged={onSelectionChangedSelected}
            readOnlyEdit={true}
            returnedRecords={selectedRawProds.length}
            height={'100%'}
          />
        </StyledTableWrapper>
      </TableRowWrapper>
      <Buttons>
        {flow === 'addRawProds' || flow === 'currentRawProduct' ? (
          <div></div>
        ) : (
          <Button label='Back' variant={Variant.Secondary} onClick={() => setStep(0)} />
        )}
        <Button label='Next' testId='nextActionButton' onClick={handleNextClick} disabled={selectedRawProds.length === 0} />
      </Buttons>
    </SelectRawProdsContainer>
  );
};

export default SelectRawProds;
